import React, { useEffect, useState } from 'react';
import { usePlaidLink, PlaidLinkOnSuccess, PlaidLinkOnEvent, PlaidLinkOnExit, PlaidLinkStableEvent } from 'react-plaid-link';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { Building2, Loader2, RefreshCw } from 'lucide-react';

interface PlaidConnectButtonProps {
  householdId: string;
  onSuccess: (itemId: string, institutionName: string, accounts: any[]) => void;
  /** When set, this button reconnects an existing, already-linked item
   *  (Plaid's "update mode" -- e.g. after an ITEM_LOGIN_REQUIRED error)
   *  instead of starting a brand new bank connection. Update mode
   *  re-authenticates the SAME item_id Plaid already issued for it, so
   *  every local account's persisted plaidAccountId/plaidItemId stays
   *  valid afterward -- no account-mapping step needed, unlike a fresh
   *  "Connect Bank" flow which always creates a new item. */
  reconnectItemId?: string;
  reconnectInstitutionName?: string;
  onReconnectSuccess?: (institutionName: string) => void;
}

export default function PlaidConnectButton({ householdId, onSuccess, reconnectItemId, reconnectInstitutionName, onReconnectSuccess }: PlaidConnectButtonProps) {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loadingToken, setLoadingToken] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The real reason behind Plaid Link's own generic "Something went wrong"
  // screen -- previously discarded entirely (see logPlaidLinkEvent's
  // comment in functions/src/plaid.ts). Shown directly under the button so
  // it's visible without needing to open devtools, and also logged
  // server-side so it's checkable after the fact.
  const [linkErrorDetail, setLinkErrorDetail] = useState<string | null>(null);
  // Temporary, reconnect-only diagnostic: asks Plaid directly for this
  // item's own persisted status (plaidDiagnoseItem), rather than relying
  // on whatever error the last Link attempt happened to surface. Safe,
  // non-secret fields only -- see functions/src/plaid.ts.
  const [diagnosis, setDiagnosis] = useState<string | null>(null);
  const [diagnosing, setDiagnosing] = useState(false);

  const runDiagnosis = async () => {
    if (!reconnectItemId) return;
    setDiagnosing(true);
    setDiagnosis(null);
    try {
      const diagnose = httpsCallable(functions, 'plaidDiagnoseItem');
      const response = await diagnose({ itemId: reconnectItemId }) as { data: any };
      setDiagnosis(JSON.stringify(response.data, null, 2));
    } catch (err: any) {
      setDiagnosis(`Diagnosis failed: ${err.message || err}`);
    } finally {
      setDiagnosing(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchToken = async () => {
      setLoadingToken(true);
      try {
        const createToken = httpsCallable(functions, 'plaidCreateLinkToken');
        const response = await createToken(reconnectItemId ? { itemId: reconnectItemId } : {}) as { data: { link_token: string } };
        if (mounted) {
          setLinkToken(response.data.link_token);
        }
      } catch (err: any) {
        console.error('Error fetching link token', err);
        if (mounted) {
          setError(err.message || 'Failed to initialize bank connection.');
        }
      } finally {
        if (mounted) {
          setLoadingToken(false);
        }
      }
    };
    fetchToken();
    return () => { mounted = false; };
  }, [reconnectItemId]);

  const handleOnSuccess: PlaidLinkOnSuccess = async (public_token, metadata) => {
    try {
      const exchangeToken = httpsCallable(functions, 'plaidExchangePublicToken');
      const response = await exchangeToken({ publicToken: public_token, householdId }) as { data: { itemId: string, institutionName: string, accounts: any[], reconnected: boolean } };
      if (response.data.reconnected) {
        onReconnectSuccess?.(response.data.institutionName);
      } else {
        onSuccess(response.data.itemId, response.data.institutionName, response.data.accounts);
      }
    } catch (err) {
      console.error('Error exchanging public token', err);
      alert('There was an issue linking your bank. Please try again.');
    }
  };

  const logLinkEvent = (eventName: string, metadata: any) => {
    try {
      const logEvent = httpsCallable(functions, 'logPlaidLinkEvent');
      void logEvent({ eventName, metadata, itemId: reconnectItemId || null });
    } catch (e) {
      console.error('Failed to log Plaid Link event', e);
    }
  };

  const handleOnEvent: PlaidLinkOnEvent = (eventName, metadata) => {
    if (eventName === PlaidLinkStableEvent.ERROR) {
      console.error('Plaid Link ERROR event:', eventName, metadata);
      logLinkEvent(eventName, metadata);
      if (metadata.error_message) {
        setLinkErrorDetail(`${metadata.error_code || 'Error'}: ${metadata.error_message}`);
      }
    }
  };

  const handleOnExit: PlaidLinkOnExit = (err, metadata) => {
    if (err) {
      console.warn('Plaid link exited with error:', err, metadata);
      logLinkEvent('EXIT_WITH_ERROR', { error: err, metadata });
      setLinkErrorDetail(err.display_message || `${err.error_code}: ${err.error_message}`);
    }
  };

  const { open, ready } = usePlaidLink({
    token: linkToken!,
    onSuccess: handleOnSuccess,
    onEvent: handleOnEvent,
    onExit: handleOnExit,
  });

  if (error) {
    return (
      <button disabled className="flex items-center gap-2 bg-slate-100 text-slate-500 px-4 py-2 rounded-lg text-sm font-medium">
        <Building2 className="w-4 h-4" />
        Connection Unavailable
      </button>
    );
  }

  if (reconnectItemId) {
    return (
      <div className="flex flex-col gap-1.5">
        <button
          onClick={() => open()}
          disabled={!ready || loadingToken}
          className="flex items-center gap-2 bg-amber-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors"
        >
          {loadingToken ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {loadingToken ? 'Loading…' : `Reconnect ${reconnectInstitutionName || 'Bank'}`}
        </button>
        {linkErrorDetail && (
          <p className="text-xs text-rose-700 max-w-xs">{linkErrorDetail}</p>
        )}
        <button
          onClick={runDiagnosis}
          disabled={diagnosing}
          className="text-xs text-slate-500 underline text-left disabled:opacity-50 w-fit"
        >
          {diagnosing ? 'Checking with Plaid…' : 'Check status with Plaid'}
        </button>
        {diagnosis && (
          <pre className="text-[10px] bg-slate-50 border border-slate-200 rounded p-2 max-w-sm overflow-auto whitespace-pre-wrap">{diagnosis}</pre>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <button
        onClick={() => open()}
        disabled={!ready || loadingToken}
        className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors"
      >
        {loadingToken ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
        {loadingToken ? 'Loading...' : 'Connect Bank'}
      </button>
      {linkErrorDetail && (
        <p className="text-xs text-rose-700 max-w-xs">{linkErrorDetail}</p>
      )}
    </div>
  );
}
